import React, { useEffect, useState } from "react";
import {
  X, ShieldCheck, Lock, CreditCard, ArrowRight, Sparkles,
} from "lucide-react";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { useToast } from "../hooks/use-toast";

/**
 * Modal di Checkout riutilizzabile per Packages (home) e CoursePage.
 *
 * Props:
 *  - pacchetto: { id, name/title, price, items:[{label, price, free}] , tagline? }
 *  - onSubmit: async (billing) => void — parent gestisce popup certificazioni/checkout API
 *  - onClose: () => void
 *  - submitting: bool
 *  - notice?: nodo opzionale sotto il titolo (es. warning "non-socio")
 */
export default function CheckoutModal({
  pacchetto,
  onSubmit,
  onClose,
  submitting = false,
  notice = null,
}) {
  const { toast } = useToast();
  const [form, setForm] = useState({
    nome: "", cognome: "", email: "", telefono: "",
    ragioneSociale: "",
    indirizzo: "", citta: "", cap: "", provincia: "",
    codiceFiscale: "", partitaIva: "",
    pec: "", sdi: "",
    accetto: false,
  });

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Chiudi con ESC + lock scroll body
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !submitting && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, submitting]);

  if (!pacchetto) return null;

  const nome = pacchetto.name || pacchetto.title;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.nome || !form.cognome || !form.email) {
      toast({ description: "Compila nome, cognome ed email." });
      return;
    }
    if (!form.indirizzo || !form.citta || !form.cap || !form.provincia) {
      toast({ description: "Indirizzo, città, CAP e provincia sono richiesti." });
      return;
    }
    if (!form.codiceFiscale && !form.partitaIva) {
      toast({ description: "Inserisci Codice Fiscale o P.IVA." });
      return;
    }
    if (!form.accetto) {
      toast({ description: "Accetta lo statuto per procedere." });
      return;
    }
    const billing = {
      nome: form.nome,
      cognome: form.cognome,
      email: form.email,
      telefono: form.telefono || undefined,
      indirizzo: form.indirizzo,
      citta: form.citta,
      cap: form.cap,
      provincia: form.provincia.toUpperCase(),
      codice_fiscale: form.codiceFiscale || undefined,
      partita_iva: form.partitaIva || undefined,
      ragione_sociale: form.ragioneSociale || undefined,
      pec: form.pec || undefined,
      sdi: form.sdi || undefined,
    };
    onSubmit(billing);
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-start md:items-center justify-center p-4 md:p-8 overflow-y-auto"
      data-testid="checkout-modal"
      onClick={(e) => e.target === e.currentTarget && !submitting && onClose()}
    >
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-5xl my-auto animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-[#0a1f55] to-[#102a6b] text-white rounded-t-3xl px-6 md:px-8 py-5 flex items-center justify-between">
          <div>
            <div className="text-[10.5px] font-extrabold uppercase tracking-[0.22em] text-[#fbbf24]">
              Pagamento sicuro · A.C.D.T.
            </div>
            <h2 className="mt-1 text-xl md:text-2xl font-extrabold leading-tight">
              {nome}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            data-testid="checkout-modal-close"
            className="p-2 rounded-full hover:bg-white/10 disabled:opacity-40 transition-colors"
            aria-label="Chiudi"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 md:p-8">
          {notice && <div className="mb-6">{notice}</div>}

          <div className="grid lg:grid-cols-12 gap-8">
            {/* LEFT — Form fatturazione */}
            <div className="lg:col-span-7 space-y-4">
              <div>
                <h3 className="text-lg font-extrabold text-[#0a1f55]">Dati di fatturazione</h3>
                <p className="text-sm text-slate-500 mt-0.5">
                  Necessari per emettere la ricevuta / fattura del pagamento.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <ModField label="Nome *" testid="cm-nome" value={form.nome} onChange={onChange("nome")} />
                <ModField label="Cognome *" testid="cm-cognome" value={form.cognome} onChange={onChange("cognome")} />
                <ModField label="Email *" type="email" testid="cm-email" value={form.email} onChange={onChange("email")} />
                <ModField label="Telefono" testid="cm-tel" value={form.telefono} onChange={onChange("telefono")} />
                <div className="sm:col-span-2">
                  <ModField label="Ragione sociale (se diversa)" testid="cm-rs" value={form.ragioneSociale} onChange={onChange("ragioneSociale")} placeholder="Lascia vuoto se persona fisica" />
                </div>
                <div className="sm:col-span-2">
                  <ModField label="Indirizzo *" testid="cm-ind" value={form.indirizzo} onChange={onChange("indirizzo")} placeholder="Via Roma 1" />
                </div>
                <ModField label="Città *" testid="cm-citta" value={form.citta} onChange={onChange("citta")} />
                <div className="grid grid-cols-2 gap-2">
                  <ModField label="CAP *" testid="cm-cap" value={form.cap} onChange={(e) => setForm({ ...form, cap: e.target.value.replace(/\D/g, "").slice(0, 5) })} maxLength={5} />
                  <ModField label="Prov. *" testid="cm-prov" value={form.provincia} onChange={(e) => setForm({ ...form, provincia: e.target.value.toUpperCase().slice(0, 2) })} maxLength={2} placeholder="MI" />
                </div>
                <ModField label="Codice Fiscale" testid="cm-cf" value={form.codiceFiscale} onChange={(e) => setForm({ ...form, codiceFiscale: e.target.value.toUpperCase() })} placeholder="RSSMRA80A01H501Z" />
                <ModField label="Partita IVA" testid="cm-piva" value={form.partitaIva} onChange={(e) => setForm({ ...form, partitaIva: e.target.value.replace(/\D/g, "") })} placeholder="12345678901" />
                <div className="sm:col-span-2 -mt-2 text-[11.5px] text-slate-500">
                  Inserisci almeno uno tra Codice Fiscale e Partita IVA.
                </div>
                <details className="sm:col-span-2 rounded-lg border border-slate-200 p-3 text-[12.5px]">
                  <summary className="cursor-pointer font-bold text-[#0a1f55]">
                    Fatturazione elettronica (PEC / SDI) — opzionale
                  </summary>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <ModField label="PEC" testid="cm-pec" value={form.pec} onChange={onChange("pec")} placeholder="azienda@pec.it" />
                    <ModField label="Codice SDI" testid="cm-sdi" value={form.sdi} onChange={(e) => setForm({ ...form, sdi: e.target.value.toUpperCase().slice(0, 7) })} maxLength={7} placeholder="XXXXXXX" />
                  </div>
                </details>
              </div>

              {/* Modalità di pagamento */}
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                  <div className="text-[13px] text-emerald-900 flex-1">
                    <div className="font-extrabold mb-1">Modalità di pagamento accettate</div>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                      <MetodoBadge label="Carta" />
                      <MetodoBadge label="PayPal" />
                      <MetodoBadge label="Klarna" hint="3 rate 0%" />
                      <MetodoBadge label="Apple Pay" />
                      <MetodoBadge label="Google Pay" />
                    </div>
                    <div className="mt-2 text-[12px] text-emerald-800">
                      Verrai reindirizzato al checkout Stripe. Riceverai <strong>ricevuta/fattura PDF via email</strong>.
                    </div>
                  </div>
                </div>
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={form.accetto}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, accetto: Boolean(v) }))}
                  data-testid="cm-accetto"
                  className="mt-0.5"
                />
                <span className="text-[12.5px] text-slate-600 leading-relaxed">
                  Accetto i termini, lo <strong>Statuto</strong>, il Codice Deontologico e il trattamento
                  dei dati personali ai sensi del GDPR.
                </span>
              </label>
            </div>

            {/* RIGHT — Riepilogo sticky */}
            <aside className="lg:col-span-5">
              <div className="lg:sticky lg:top-24 rounded-2xl border border-slate-200 bg-gradient-to-br from-[#f8faff] to-white p-5 md:p-6">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-extrabold text-[#0a1f55] uppercase tracking-wider">
                    Riepilogo
                  </h4>
                  <span className="text-[10.5px] font-extrabold uppercase tracking-widest text-[#f59e0b] max-w-[60%] text-right">
                    {nome}
                  </span>
                </div>

                <div className="mt-4 space-y-2.5 border-t border-dashed border-slate-200 pt-4">
                  {(pacchetto.items || []).map((it, idx) => (
                    <div key={`${it.label}-${idx}`} className="flex items-start justify-between gap-3 text-[13px]">
                      <span className="text-slate-700 pr-1">{it.label}</span>
                      <span className="font-semibold text-[#0a1f55] whitespace-nowrap">
                        {it.free ? (
                          <span className="text-[#f59e0b]">Incluso</span>
                        ) : (
                          `€ ${Number(it.price || 0).toFixed(2).replace(".", ",")}`
                        )}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="mt-5 pt-5 border-t border-slate-200 flex items-baseline justify-between">
                  <span className="text-sm font-semibold text-slate-600">Totale</span>
                  <span className="text-3xl font-extrabold text-[#102a6b]">
                    € {Number(pacchetto.price || pacchetto.total || 0).toFixed(2).replace(".", ",")}
                  </span>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="checkout-modal-submit"
                  className="mt-5 w-full h-12 bg-[#102a6b] hover:bg-[#0a1f55] text-white font-bold text-base"
                >
                  {submitting ? (
                    "Apertura pagamento…"
                  ) : (
                    <>
                      <CreditCard className="h-4 w-4 mr-2" />
                      Paga ora <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>

                <div className="mt-3 grid grid-cols-2 gap-2 text-[11.5px] text-slate-500">
                  <div className="flex items-center gap-1.5">
                    <Lock className="h-3 w-3 text-[#102a6b]" /> SSL sicuro
                  </div>
                  <div className="flex items-center gap-1.5">
                    <ShieldCheck className="h-3 w-3 text-[#102a6b]" /> Conforme GDPR
                  </div>
                </div>

                <div className="mt-4 text-[11px] text-slate-500 leading-relaxed border-t border-slate-100 pt-3">
                  <Sparkles className="inline h-3 w-3 text-[#f59e0b] mr-1" />
                  Pagamento <strong>una tantum</strong>. Nessun rinnovo automatico.
                </div>
              </div>
            </aside>
          </div>
        </form>
      </div>
    </div>
  );
}

function ModField({ label, value, onChange, testid, ...rest }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-bold text-slate-700 mb-1">{label}</span>
      <input
        {...rest}
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="w-full h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#102a6b]/30 focus:border-[#102a6b] transition-colors"
      />
    </label>
  );
}

function MetodoBadge({ label, hint }) {
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-white border border-emerald-200 text-emerald-900 text-[11.5px] font-bold">
      {label}
      {hint && <span className="text-emerald-600 font-normal">· {hint}</span>}
    </span>
  );
}
